/**
 * Helpers for showing specialities organised by their group in a PrimeNG
 * multi-select (`[group]="true"`, optionGroupLabel="label",
 * optionGroupChildren="items").
 *
 * Rows come from SettingsService.getSpecialties, which embeds the parent as
 * `group: { id, name } | null`. Groups themselves (is_group = true) are never
 * selectable options, only headers.
 */

export interface SpecialityGroupOption<T = any> {
  /** Group id, or null for the "without group" bucket. */
  id: number | null;
  label: string;
  items: T[];
  /** Set on the synthetic bucket that lists what is currently selected. */
  kind?: 'selected';
}

export interface GroupedSpecialities<T = any> {
  /** False when the branch has no groups at all: render a flat list instead. */
  grouped: boolean;
  groups: SpecialityGroupOption<T>[];
}

/**
 * Buckets specialities under their group. Groups are ordered by name, the
 * ungrouped bucket (if any) comes last under `ungroupedLabel`.
 */
export function groupSpecialities<T extends { id: number; name: string; is_group?: boolean; group?: { id: number; name: string } | null }>(
  specialities: T[],
  ungroupedLabel: string
): GroupedSpecialities<T> {
  const selectable = (specialities || []).filter(s => !s.is_group);
  const byGroup = new Map<number, SpecialityGroupOption<T>>();
  const ungrouped: T[] = [];

  for (const s of selectable) {
    if (s.group?.id) {
      let bucket = byGroup.get(s.group.id);
      if (!bucket) {
        bucket = { id: s.group.id, label: s.group.name, items: [] };
        byGroup.set(s.group.id, bucket);
      }
      bucket.items.push(s);
    } else {
      ungrouped.push(s);
    }
  }

  if (byGroup.size === 0) {
    return { grouped: false, groups: [{ id: null, label: ungroupedLabel, items: selectable }] };
  }

  const groups = [...byGroup.values()].sort((a, b) => a.label.localeCompare(b.label));
  for (const g of groups) g.items.sort((a, b) => a.name.localeCompare(b.name));
  if (ungrouped.length) {
    ungrouped.sort((a, b) => a.name.localeCompare(b.name));
    groups.push({ id: null, label: ungroupedLabel, items: ungrouped });
  }
  return { grouped: true, groups };
}

/**
 * True when every item of the group is in `selected`. `valueOf` maps an option
 * to what the form control actually stores (the id, or the object itself).
 */
export function isGroupFullySelected<T, V>(
  group: SpecialityGroupOption<T>,
  selected: V[] | null | undefined,
  valueOf: (item: T) => V
): boolean {
  if (!group.items.length) return false;
  const set = new Set(selected || []);
  return group.items.every(i => set.has(valueOf(i)));
}

/**
 * Returns the new selection after toggling a whole group: if every member is
 * already selected they are all removed, otherwise the missing ones are added.
 * Order of what was already selected is preserved.
 */
export function toggleGroupSelection<T, V>(
  group: SpecialityGroupOption<T>,
  selected: V[] | null | undefined,
  valueOf: (item: T) => V
): V[] {
  const current = [...(selected || [])];
  const members = group.items.map(valueOf);
  if (isGroupFullySelected(group, current, valueOf)) {
    const drop = new Set(members);
    return current.filter(v => !drop.has(v));
  }
  const have = new Set(current);
  for (const m of members) if (!have.has(m)) current.push(m);
  return current;
}

/**
 * Moves the currently selected items into a synthetic first bucket labelled
 * `selectedLabel`, so they can be reviewed and deselected without scrolling.
 * Groups left empty disappear; with nothing selected the input is returned
 * untouched (a flat list stays flat).
 */
export function withSelectedOnTop<T, V>(
  grouped: GroupedSpecialities<T>,
  selected: V[] | null | undefined,
  valueOf: (item: T) => V,
  selectedLabel: string
): GroupedSpecialities<T> {
  if (!selected?.length) return grouped;
  const chosen = new Set(selected);
  const picked: T[] = [];
  const rest: SpecialityGroupOption<T>[] = [];
  for (const g of grouped.groups) {
    const keep: T[] = [];
    for (const item of g.items) (chosen.has(valueOf(item)) ? picked : keep).push(item);
    if (keep.length) rest.push({ ...g, items: keep });
  }
  if (!picked.length) return grouped;
  // Keep the user's selection order so the newest pick appears last.
  const order = new Map<V, number>(); selected.forEach((v, i) => order.set(v, i));
  picked.sort((a, b) => (order.get(valueOf(a)) ?? 0) - (order.get(valueOf(b)) ?? 0));
  return { grouped: true, groups: [{ id: null, kind: 'selected', label: selectedLabel, items: picked }, ...rest] };
}
